/**
 * Custom Form Comparison Utilities
 *
 * Shared utilities for detecting changes in custom form fields across all survey types.
 * These comparisons focus on user-editable properties to avoid false positives from
 * React Hook Form's internal tracking properties.
 *
 * ## Problem Solved
 * React Hook Form's isDirty flag doesn't always detect custom field changes because:
 * 1. Forms are initialized with setValue() instead of reset()
 * 2. Custom form fields are deeply nested objects
 * 3. We only care about user-visible changes, not internal metadata
 *
 * ## Solution
 * Property-specific comparison that:
 * - Compares only user-editable fields (label, fieldType, isOptional, options)
 * - Ignores internal properties (id, __typename, React Hook Form metadata)
 * - Handles undefined/null gracefully
 * - Returns false when no fields exist (nothing to save)
 *
 * ## DRY Principle
 * This utility eliminates 140+ lines of duplicated code across 4 survey types:
 * - CustomSurvey
 * - EventRegistrationSurvey
 * - SatisfactionSurvey
 * - DonationSurvey
 *
 * ## Performance
 * - Time Complexity: O(n * m) where n = number of fields, m = number of options
 * - Space Complexity: O(1) - no additional data structures created
 * - Early returns optimize common cases (empty arrays, length differences)
 * - Should be wrapped in React.useMemo() by caller to avoid recalculation
 */

import type * as Api from '@ViewModels';

/**
 * Compares two custom form field arrays to detect meaningful user changes
 *
 * ## What Gets Compared
 * Only compares properties that users can actually edit:
 * - **Field label**: Question text (e.g., "What is your name?")
 * - **Field type**: Input type (String, Option, etc.)
 * - **Required/optional**: isOptional flag
 * - **Option labels**: For dropdown/checkbox fields
 *
 * ## What Gets Ignored
 * Intentionally ignores to prevent false positives:
 * - Field IDs (internal tracking)
 * - Field order/position (not yet implemented)
 * - React Hook Form metadata
 * - GraphQL __typename fields
 * - Timestamps, creation dates
 * - Any other non-user-editable properties
 *
 * ## Edge Cases Handled
 * - **undefined/null arrays**: Treated as empty arrays
 * - **Empty current fields**: Returns false (nothing to save)
 * - **Length differences**: Immediately returns true (fields added/removed)
 * - **Missing options**: Treated as empty arrays
 * - **Tab switching**: No false positives (only compares user-editable props)
 *
 * ## Known Limitations
 * - **Option reordering**: Not detected (same options, different order = no change)
 * - **Whitespace**: "Question" vs "Question " are different
 * - **Case sensitivity**: "Question" vs "question" are different
 *
 * These limitations are intentional - they're rare edge cases that would
 * add complexity without significant benefit. Can be added if users report issues.
 *
 * @param originalFields - Original field array from saved survey data (survey?.customForm?.fields)
 * @param currentFields - Current field array from form state (formValues.customForm?.fields)
 * @returns true if there are user-visible changes, false otherwise
 *
 * @example Basic usage (should be memoized)
 * ```typescript
 * const customFormFieldsHaveChanges = React.useMemo(
 *   () => hasCustomFormChanges(survey?.customForm?.fields, formValues.customForm?.fields),
 *   [survey?.customForm?.fields, formValues.customForm?.fields]
 * );
 * ```
 *
 * @example Use with useUnsavedChanges hook
 * ```typescript
 * const hasUnsavedChanges = useUnsavedChanges({
 *   surveyType: 'custom',
 *   isEdit: true,
 *   isClone: false,
 *   formState,
 *   additionalConditions: [customFormFieldsHaveChanges],
 * });
 * ```
 */
export const hasCustomFormChanges = (
	originalFields: Api.IFormField<string>[] | undefined,
	currentFields: Api.IFormField<string>[] | undefined
): boolean => {
	// Normalize undefined to empty arrays for easier comparison
	// This prevents null/undefined checks throughout the function
	const original = originalFields || [];
	const current = currentFields || [];

	// Edge case: If there are no current fields, nothing to save
	// This prevents false positives when user deletes all questions
	// User intention: "I don't want any questions" - not an unsaved change
	if (current.length === 0) {
		return false;
	}

	// Quick check: If array lengths differ, definitely have changes
	// Covers: adding questions (0→1, 1→2) or removing questions (2→1)
	// Early return optimizes common case
	if (original.length !== current.length) {
		return true;
	}

	// Deep comparison: Check each field's user-editable properties
	// Uses .some() to return true on first difference found (short-circuit)
	return original.some((originalField, index) => {
		const currentField = current[index];

		// Safety check: If current field is missing at this index, it's a change
		// This shouldn't happen given the length check above, but handles edge cases
		if (!currentField) return true;

		// Compare field label (question text)
		// Example: "What is your name?" → "What is your full name?"
		if (originalField.label !== currentField.label) return true;

		// Compare field type
		// Example: String (text input) → Option (dropdown)
		if (originalField.fieldType !== currentField.fieldType) return true;

		// Compare required/optional status
		// Example: required (isOptional=false) → optional (isOptional=true)
		if (originalField.isOptional !== currentField.isOptional) return true;

		// Compare options for fields that support them (dropdown, checkbox, radio)
		// Normalize undefined options to empty arrays
		const originalOptions = originalField.options || [];
		const currentOptions = currentField.options || [];

		// Quick check: If option counts differ, definitely changed
		// Example: 2 options → 3 options (added one)
		if (originalOptions.length !== currentOptions.length) return true;

		// Compared as an unordered multiset. This checks the form against the SERVER's copy, and
		// option order is tracked by the field state manager instead, which is re-baselined when a
		// save succeeds. An index-positional compare here reported a pure reorder as a difference
		// the server copy never caught up to, pinning the unsaved-changes banner open after save.
		const sortedLabels = (options: Api.CustomFormFieldOption[]) => options.map(option => option?.label ?? '').sort();
		const originalLabels = sortedLabels(originalOptions);
		const currentLabels = sortedLabels(currentOptions);
		return currentLabels.some((label, optIndex) => label !== originalLabels[optIndex]);
	});
};
